import { Link, createFileRoute, notFound } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { authClient } from '../../auth/client'
import { SiteFooter } from '../../components/SiteFooter'
import { SiteHeader } from '../../components/SiteHeader'
import { useLocale } from '../../components/use-locale'
import type { LikeNotification } from '../../db/queries'
import { fmt, getDict, intlTag, parseLocale } from '../../i18n'
import { formatDuration } from '../../lib/format'
import { seoHead } from '../../lib/seo'
import { SITE_ORIGIN } from '../../lib/site'

export const Route = createFileRoute('/{-$locale}/notifications')({
  beforeLoad: ({ params }) => {
    if (!parseLocale(params.locale)) throw notFound()
  },
  head: ({ params }) => {
    const locale = parseLocale(params.locale) ?? 'ja'
    const t = getDict(locale)
    return seoHead({
      origin: SITE_ORIGIN,
      locale,
      path: '/notifications',
      title: t.meta.notificationsTitle,
      description: t.notifications.empty,
      noindex: true,
    })
  },
  component: NotificationsPage,
})

/** 通知: 自分のレシートに付いたいいね */
function NotificationsPage() {
  const { t, locale, path } = useLocale()
  const session = authClient.useSession()
  const user = session.data?.user as { id: string; isAnonymous?: boolean | null } | undefined
  const [items, setItems] = useState<LikeNotification[] | null>(null)

  useEffect(() => {
    if (!user) return
    let alive = true
    fetch('/api/notifications')
      .then((r) => (r.ok ? (r.json() as Promise<LikeNotification[]>) : []))
      .then((ns) => {
        if (!alive) return
        setItems(ns)
        // 開いた時点までのいいねを既読にする。この画面では新着の印を残す
        if (ns.some((n) => n.unread)) void fetch('/api/notifications/read', { method: 'POST' })
      })
      .catch(() => {
        if (alive) setItems([])
      })
    return () => {
      alive = false
    }
  }, [user?.id])

  const dateFmt = new Intl.DateTimeFormat(intlTag(locale), { month: 'short', day: 'numeric' })
  const whenFmt = new Intl.DateTimeFormat(intlTag(locale), {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

  let body: React.ReactNode
  if (session.isPending || (user && !items))
    body = <div className="account__placeholder" aria-hidden="true" />
  else if (!user)
    body = (
      <>
        <p className="lp-body">{t.notifications.signedOut}</p>
        <Link to={path('/cafe')} className="btn btn--lamp">
          {t.nav.enter}
        </Link>
      </>
    )
  else
    body = (
      <>
        {user.isAnonymous && <p className="account__note">{t.notifications.guestNote}</p>}
        {items?.length === 0 ? (
          <p className="lp-body">{t.notifications.empty}</p>
        ) : (
          <ul className="notifications__list">
            {items?.map((n) => {
              const receipt = fmt(t.notifications.receipt, {
                date: dateFmt.format(n.logEndedAt),
                duration: formatDuration(n.focusSeconds, locale),
              })
              return (
                <li
                  key={`${n.logId}:${n.liker.handle ?? n.liker.name}:${n.likedAt}`}
                  className={
                    n.unread ? 'notifications__item notifications__item--unread' : 'notifications__item'
                  }
                >
                  <Link to={path(`/log/${n.logId}`)} className="notifications__link">
                    <svg className="notifications__icon" viewBox="0 0 20 20" aria-hidden="true">
                      <path d="M10 17s-6.5-4-6.5-8.5A3.5 3.5 0 0 1 10 6a3.5 3.5 0 0 1 6.5 2.5C16.5 13 10 17 10 17z" />
                    </svg>
                    <span className="notifications__body">
                      <span className="notifications__text">
                        {fmt(t.notifications.liked, { name: n.liker.name, receipt })}
                      </span>
                      {n.note && <span className="notifications__note">{n.note}</span>}
                      <span className="notifications__when">
                        {n.unread && <span className="notifications__new">{t.notifications.new}</span>}
                        {whenFmt.format(n.likedAt)}
                      </span>
                    </span>
                  </Link>
                </li>
              )
            })}
          </ul>
        )}
        <Link to={path('/cafe')} className="account__link">
          {t.account.backToCafe}
        </Link>
      </>
    )

  return (
    <>
      <SiteHeader variant="solid" />
      <main id="main" className="account notifications">
        <h1 className="legal__title">{t.notifications.title}</h1>
        {body}
      </main>
      <SiteFooter />
    </>
  )
}
